import {
  Component,
  ChangeDetectionStrategy,
  inject,
  input,
  output,
  signal,
  computed,
  effect
} from '@angular/core';

import { FormsModule, ReactiveFormsModule, FormGroup, FormControl, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { DrawerModule } from 'primeng/drawer';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { CardModule } from 'primeng/card';
import { TooltipModule } from 'primeng/tooltip';
import { MessageModule } from 'primeng/message';
import { ChipModule } from 'primeng/chip';
import { TelecomService } from '@generated/services/telecom.service';
import { TelecomAccountListItemResult } from '@generated/models/telecom-account-list-item-result';
import { TelecomAccountResult } from '@generated/models/telecom-account-result';
import { SipGatewayInfo } from '@generated/models/sip-gateway-info';
import { SipTransport } from '@generated/models/sip-transport';
import { NotificationService } from '@core/services/notification.service';
import { SipGatewayInfoComponent } from '@shared/components/sip-gateway-info/sip-gateway-info';
import { environment } from '../../../../environments/environment';
import { IMPORT_NUMBER_FIELDS } from '@core/assistant/assistant-catalog';
import { AssistantActionDirective } from '@shared/directives/assistant-action.directive';
import { AssistantEditableDirective } from '@shared/directives/assistant-editable.directive';
import { AssistantSurfaceDirective } from '@shared/directives/assistant-surface.directive';

const E164_RE = /^\+[1-9]\d{1,14}$/;
const COUNTRY_RE = /^[A-Z]{2}$/;
const IPV4_OR_CIDR_RE = /^((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)\.){3}(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\/(3[0-2]|[12]?\d))?$/;

function pairValidator(userKey: string, pwdKey: string, errKey: string) {
  return (group: AbstractControl): ValidationErrors | null => {
    const u = group.get(userKey)?.value as string | null;
    const p = group.get(pwdKey)?.value as string | null;
    const uSet = !!(u && u.trim());
    const pSet = !!(p && p.trim());
    return uSet !== pSet ? { [errKey]: true } : null;
  };
}

@Component({
  selector: 'app-import-custom-sip-number',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    DrawerModule,
    ButtonModule,
    InputTextModule,
    SelectModule,
    CardModule,
    TooltipModule,
    MessageModule,
    ChipModule,
    SipGatewayInfoComponent,
    AssistantSurfaceDirective,
    AssistantEditableDirective,
    AssistantActionDirective
],
  templateUrl: './import-custom-sip-number.html',
  styleUrl: './import-custom-sip-number.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ImportCustomSipNumberComponent {
  /** Идентификаторы полей для агента: опечатку ловит компилятор. */
  protected readonly F = IMPORT_NUMBER_FIELDS;
  private telecomService = inject(TelecomService);
  private notificationService = inject(NotificationService);

  readonly visible = input<boolean>(false);
  readonly accounts = input<TelecomAccountListItemResult[]>([]);
  readonly visibleChange = output<boolean>();
  readonly imported = output<void>();
  readonly accountCreated = output<TelecomAccountResult>();

  /** Название приложения для тайтлов и хинтов — чтоб UI не тёк по бренду. */
  readonly appName = environment.appName;

  readonly customAccounts = computed(() =>
    (this.accounts() || []).filter(a => a.provider === 'Custom')
  );
  readonly customAccountOptions = computed(() =>
    this.customAccounts().map(a => ({
      label: a.account_identifier || a.id,
      value: a.id
    }))
  );

  readonly selectedAccountId = signal<string | null>(null);
  readonly showAccountForm = signal<boolean>(false);
  readonly isCreatingAccount = signal<boolean>(false);
  readonly createdAccount = signal<TelecomAccountResult | null>(null);

  readonly phoneNumber = signal<string>('');
  readonly label = signal<string>('');
  readonly isImporting = signal<boolean>(false);

  // Gateway info fetched standalone — used as reference BEFORE the account
  // exists (in the create form). After create the same data is on
  // createdAccount().sip_gateway.
  readonly gatewayInfo = signal<SipGatewayInfo | null>(null);
  readonly createdGateway = computed<SipGatewayInfo | null>(() => this.createdAccount()?.sip_gateway ?? null);

  readonly transportOptions: { label: string; value: SipTransport }[] = [
    { label: 'Auto', value: 'Auto' },
    { label: 'UDP', value: 'Udp' },
    { label: 'TCP', value: 'Tcp' },
    { label: 'TLS', value: 'Tls' }
  ];

  readonly accountForm = new FormGroup(
    {
      name: new FormControl<string>('', { nonNullable: true, validators: [Validators.required, Validators.maxLength(120)] }),
      sipServerAddress: new FormControl<string>('', { nonNullable: true, validators: [Validators.maxLength(255)] }),
      sipTransport: new FormControl<SipTransport>('Auto', { nonNullable: true, validators: [Validators.required] }),
      sipDestinationCountry: new FormControl<string>('', { nonNullable: true, validators: [Validators.required, Validators.pattern(COUNTRY_RE)] }),
      sipAllowedAddresses: new FormControl<string>('', { nonNullable: true, validators: [Validators.required] }),
      // Outbound (LiveKit → provider): optional — both set = Digest auth,
      // both empty = IP-trust mode (Mango SIP-trunk, Plivo, etc.). Pair invariant.
      sipOutboundAuthUsername: new FormControl<string>('', { nonNullable: true }),
      sipOutboundAuthPassword: new FormControl<string>('', { nonNullable: true }),
      // Inbound (provider → LiveKit): optional — most providers don't challenge,
      // IP-allowlist is the gate. Same pair invariant.
      sipInboundAuthUsername: new FormControl<string>('', { nonNullable: true }),
      sipInboundAuthPassword: new FormControl<string>('', { nonNullable: true })
    },
    {
      validators: [
        pairValidator('sipOutboundAuthUsername', 'sipOutboundAuthPassword', 'outboundAuthPair'),
        pairValidator('sipInboundAuthUsername', 'sipInboundAuthPassword', 'inboundAuthPair')
      ]
    }
  );

  readonly allowedAddressesInvalid = signal<string[]>([]);

  readonly canImport = computed(() => {
    const num = this.phoneNumber().trim();
    return (
      !!this.selectedAccountId() &&
      E164_RE.test(num) &&
      !this.isImporting()
    );
  });

  constructor() {
    // Reset form state when drawer becomes hidden.
    effect(() => {
      if (!this.visible()) {
        this.resetState();
      }
    });
  }

  private resetState(): void {
    this.selectedAccountId.set(null);
    this.showAccountForm.set(false);
    this.createdAccount.set(null);
    this.phoneNumber.set('');
    this.label.set('');
    this.accountForm.reset({ sipTransport: 'Auto' });
    this.allowedAddressesInvalid.set([]);
  }

  close(): void {
    this.visibleChange.emit(false);
  }

  openCreateAccount(): void {
    this.showAccountForm.set(true);
    if (!this.gatewayInfo()) {
      this.loadGatewayInfo();
    }
  }

  private loadGatewayInfo(): void {
    this.telecomService.apiTelecomSipGatewayGet().subscribe({
      next: (info) => this.gatewayInfo.set(info),
      error: (error) => console.error('Failed to load SIP gateway info:', error)
    });
  }

  cancelCreateAccount(): void {
    this.showAccountForm.set(false);
    this.accountForm.reset({ sipTransport: 'Auto' });
    this.allowedAddressesInvalid.set([]);
  }

  private parseAllowedAddresses(raw: string): { values: string[]; invalid: string[] } {
    const parts = raw.split(/[\s,;]+/).map(s => s.trim()).filter(Boolean);
    const invalid = parts.filter(p => !IPV4_OR_CIDR_RE.test(p));
    return { values: parts, invalid };
  }

  createAccount(): void {
    if (this.accountForm.invalid || this.isCreatingAccount()) return;

    const raw = this.accountForm.controls.sipAllowedAddresses.value;
    const { values: allowed, invalid } = this.parseAllowedAddresses(raw);
    this.allowedAddressesInvalid.set(invalid);
    if (allowed.length === 0 || invalid.length > 0) return;

    const v = this.accountForm.getRawValue();
    const outboundUser = v.sipOutboundAuthUsername.trim();
    const outboundPwd = v.sipOutboundAuthPassword.trim();
    const inboundUser = v.sipInboundAuthUsername.trim();
    const inboundPwd = v.sipInboundAuthPassword.trim();

    this.isCreatingAccount.set(true);
    this.telecomService.apiTelecomCustomAccountsPost({
      body: {
        name: v.name.trim(),
        sip_server_address: v.sipServerAddress.trim() || null,
        sip_transport: v.sipTransport,
        sip_destination_country: v.sipDestinationCountry.trim().toUpperCase(),
        sip_allowed_addresses: allowed,
        sip_outbound_auth_username: outboundUser || null,
        sip_outbound_auth_password: outboundPwd || null,
        sip_inbound_auth_username: inboundUser || null,
        sip_inbound_auth_password: inboundPwd || null
      }
    }).subscribe({
      next: (account) => {
        this.isCreatingAccount.set(false);
        this.createdAccount.set(account);
        this.selectedAccountId.set(account.id || null);
        this.showAccountForm.set(false);
        this.accountCreated.emit(account);
        this.notificationService.showSuccess(
          $localize`:@@customSip.notify.accountCreated:Custom SIP account created`
        );
      },
      error: (error) => {
        this.isCreatingAccount.set(false);
        const msg = this.extractProblemDetailsMessage(error)
          ?? $localize`:@@customSip.notify.createFail:Failed to create Custom SIP account`;
        this.notificationService.showError(msg);
      }
    });
  }

  importNumber(): void {
    const accountId = this.selectedAccountId();
    const num = this.phoneNumber().trim();
    if (!accountId || !E164_RE.test(num)) return;

    this.isImporting.set(true);
    this.telecomService.apiTelecomPhoneNumberAgentsPost({
      body: {
        telecom_account_id: accountId,
        phone_number: num,
        label: this.label().trim() || null
      }
    }).subscribe({
      next: () => {
        this.isImporting.set(false);
        this.notificationService.showSuccess(
          $localize`:@@phoneNumbers.notify.imported:Phone number imported`
        );
        this.imported.emit();
        this.close();
      },
      error: (error) => {
        this.isImporting.set(false);
        if (error?.status === 409) {
          this.notificationService.showError(
            $localize`:@@phoneNumbers.notify.alreadyTaken:Phone number is already bound to another account`
          );
          return;
        }
        if (error?.status === 500) {
          this.notificationService.showError(
            $localize`:@@phoneNumbers.notify.livekitDown:SIP gateway is unavailable. Try again or use Reprovision on the account page.`
          );
          return;
        }
        const msg = this.extractProblemDetailsMessage(error)
          ?? $localize`:@@phoneNumbers.notify.bindFail:Failed to bind phone number`;
        this.notificationService.showError(msg);
      }
    });
  }

  private extractProblemDetailsMessage(error: unknown): string | null {
    const err = error as { error?: { detail?: string; title?: string; errors?: Record<string, string[]> } } | null;
    if (!err?.error) return null;
    const errors = err.error.errors;
    if (errors) {
      const all = Object.values(errors).flat();
      if (all.length > 0) return all.join('; ');
    }
    return err.error.detail || err.error.title || null;
  }

  // Two-way bindings via ngModel + signal helpers
  onPhoneNumberInput(value: string): void {
    this.phoneNumber.set(value);
  }
  onLabelInput(value: string): void {
    this.label.set(value);
  }
  onSelectedAccountChange(value: string | null): void {
    this.selectedAccountId.set(value);
  }

  copyText(text: string): void {
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      this.notificationService.showSuccess(
        $localize`:@@common.copied:Copied`
      );
    });
  }

  // Localized labels
  get createBtnLabel(): string {
    return this.isCreatingAccount()
      ? $localize`:@@customSip.btn.creating:Creating...`
      : $localize`:@@customSip.btn.createAccount:Create account`;
  }

  // Validation helpers for template
  get f() {
    return this.accountForm.controls;
  }
  isInvalid(controlName: keyof typeof this.f): boolean {
    const c = this.f[controlName];
    return c.invalid && (c.touched || c.dirty);
  }
  hasOutboundAuthPairError(): boolean {
    return !!this.accountForm.errors?.['outboundAuthPair'] && this.accountForm.touched;
  }
  hasInboundAuthPairError(): boolean {
    return !!this.accountForm.errors?.['inboundAuthPair'] && this.accountForm.touched;
  }
  phoneNumberInvalid(): boolean {
    const v = this.phoneNumber().trim();
    return v.length > 0 && !E164_RE.test(v);
  }
}
